import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  EMPTY,
  filter,
  Observable,
  Subject,
  takeUntil,
  timeout,
  TimeoutError,
} from 'rxjs';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { SuperSocialService } from '@/app/services/super-social/super-social.service';
import { environment } from '@/environments/environment';
import {
  applySuperSocialHandleUpdate,
  buildSuperSocialHandleDrafts,
  formatBriefTrackerNumber,
  isSuperSocialHandleDraftDirty,
  normalizeSuperSocialHandleInput,
  superSocialAccountWasUpdated,
  superSocialUpdatedDateColour,
  supportsSuperSocialAccountRefresh,
} from '../brief-tracker/brief-tracker.helpers';
import {
  BriefTrackerSuperSocialAccount,
  BriefTrackerSuperSocialPlatform,
  BriefTrackerSuperSocialProfile,
} from '../brief-tracker/brief-tracker.types';
import { AdminBriefTrackerService } from '../../services/admin-brief-tracker.service';
import { AdminSettingsContextService } from '../../services/admin-settings-context.service';
import { autoLoadOnContextKey } from '../../services/admin-settings-auto-load';

/** Orchestrator POSTs can run profile + posts + scheduling; abort client wait before UI hangs forever. */
const REFRESH_REQUEST_TIMEOUT_MS = 180000;

@Component({
  selector: 'admin-tool-super-socials',
  templateUrl: './super-socials.html',
  styleUrl: './super-socials.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperSocials {
  /** When set (creator insights), auto-lookup and hide manual UID entry. */
  embeddedCreatorId = input<string | null>(null, { alias: 'creatorId' });
  readonly isEmbedded = computed(() => !!this.embeddedCreatorId()?.trim());

  /** Same businessId used by SuperSocial lookups / creates. */
  readonly businessId = environment.superSocial.businessId;

  readonly settingsContext = inject(AdminSettingsContextService);
  readonly resolvedCreatorId = computed(
    () => this.embeddedCreatorId()?.trim() || this.settingsContext.creatorId().trim(),
  );

  searchLoading = signal(false);
  searchError = signal<string | null>(null);
  superSocialProfile = signal<BriefTrackerSuperSocialProfile | null>(null);
  superSocialRefreshLoading = signal<Record<string, boolean>>({});
  superSocialRefreshSent = signal<Record<string, boolean>>({});
  handleDrafts = signal<Record<string, string>>({});
  handleUpdateLoading = signal<Record<string, boolean>>({});

  createCreatorName = signal('');
  createCreatorLoading = signal(false);
  createTiktokHandle = signal('');
  createInstagramHandle = signal('');
  createYoutubeChannel = signal('');
  createPlatformLoading = signal<Record<BriefTrackerSuperSocialPlatform, boolean>>({
    tiktok: false,
    instagram: false,
    youtube: false,
  });
  removeAccountLoading = signal<Record<string, boolean>>({});

  readonly formatNumber = formatBriefTrackerNumber;
  readonly superSocialUpdatedDateColour = superSocialUpdatedDateColour;
  readonly supportsSuperSocialAccountRefresh = supportsSuperSocialAccountRefresh;

  readonly hasTiktok = computed(() => this.hasPlatform('tiktok'));
  readonly hasInstagram = computed(() => this.hasPlatform('instagram'));
  readonly hasYoutube = computed(() => this.hasPlatform('youtube'));

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private trackerService = inject(AdminBriefTrackerService);
  private superSocialService = inject(SuperSocialService);
  private toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);

  /** Emits account id to cancel one refresh, or `*` to cancel all. */
  private readonly cancelRefreshWork$ = new Subject<string | '*'>();
  private readonly lookupCancel$ = new Subject<void>();

  constructor() {
    autoLoadOnContextKey(
      () => this.resolvedCreatorId() || null,
      () => this.runLookup(),
    );
  }

  onCreateCreatorNameInput(value: string): void {
    this.createCreatorName.set(value);
  }

  onCreateTiktokHandleInput(value: string): void {
    this.createTiktokHandle.set(value);
  }

  onCreateInstagramHandleInput(value: string): void {
    this.createInstagramHandle.set(value);
  }

  onCreateYoutubeChannelInput(value: string): void {
    this.createYoutubeChannel.set(value);
  }

  runLookup(): void {
    const creatorId = this.resolvedCreatorId();
    if (!creatorId) {
      void this.toastNotificationService.alert({
        title: 'Missing field',
        message: 'Enter creator Firebase UID.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.cancelAllRefreshWork();
    this.lookupCancel$.next();

    this.searchLoading.set(true);
    this.searchError.set(null);
    this.superSocialProfile.set(null);
    this.handleDrafts.set({});
    this.handleUpdateLoading.set({});
    this.createTiktokHandle.set('');
    this.createInstagramHandle.set('');
    this.createYoutubeChannel.set('');

    this.trackerService
      .getSuperSocialProfile(creatorId)
      .pipe(takeUntil(this.lookupCancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.searchLoading.set(false);
          this.superSocialProfile.set(profile);
          this.handleDrafts.set(buildSuperSocialHandleDrafts(profile.accounts));
          this.cdr.markForCheck();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.searchLoading.set(false);
          const message = this.extractErrorMessage(err);
          this.searchError.set(message);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Lookup failed',
            message,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  /** Abort all in-flight refresh POSTs and updatedDate polls (also runs on Look up creator). */
  cancelAllRefreshWork(): void {
    this.cancelRefreshWork$.next('*');
    this.superSocialRefreshLoading.set({});
    this.superSocialRefreshSent.set({});
    this.cdr.markForCheck();
  }

  /** Abort refresh POST + poll for one account. */
  cancelRefreshForAccount(accountId: string): void {
    this.cancelRefreshWork$.next(accountId);
    this.superSocialRefreshLoading.update((state) => {
      const next = { ...state };
      delete next[accountId];
      return next;
    });
    this.superSocialRefreshSent.update((state) => {
      const next = { ...state };
      delete next[accountId];
      return next;
    });
    this.cdr.markForCheck();
  }

  createCreatorRecord(): void {
    const externalId = this.resolvedCreatorId();
    if (!externalId) {
      void this.toastNotificationService.alert({
        title: 'Missing field',
        message: 'Enter creator Firebase UID above first.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.createCreatorLoading.set(true);
    this.cdr.markForCheck();

    this.superSocialService
      .createCreator({
        externalId,
        businessId: this.businessId,
        name: this.createCreatorName().trim() || externalId,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.createCreatorLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Creator created',
            message: 'SuperSocial creator record saved. Reloading connections…',
            type: 'success',
            icon: 'success',
          });
          this.runLookup();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.createCreatorLoading.set(false);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Create creator failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  createTiktokAccount(): void {
    const handle = normalizeSuperSocialHandleInput(this.createTiktokHandle());
    if (!handle) {
      void this.toastNotificationService.alert({
        title: 'Missing handle',
        message: 'Enter a TikTok handle.',
        type: 'error',
        icon: 'error',
      });
      return;
    }
    this.createPlatformAccount('tiktok', () =>
      this.superSocialService.createTiktokAccountTikApi({
        externalId: this.resolvedCreatorId(),
        businessId: this.businessId,
        handle,
      }),
    );
  }

  createInstagramAccount(): void {
    const handle = normalizeSuperSocialHandleInput(this.createInstagramHandle());
    if (!handle) {
      void this.toastNotificationService.alert({
        title: 'Missing handle',
        message: 'Enter an Instagram handle.',
        type: 'error',
        icon: 'error',
      });
      return;
    }
    this.createPlatformAccount('instagram', () =>
      this.superSocialService.createInstagramAccountApify({
        externalId: this.resolvedCreatorId(),
        businessId: this.businessId,
        handle,
      }),
    );
  }

  createYoutubeAccount(): void {
    const channelIdOrHandle = this.createYoutubeChannel().trim().replace(/^@/, '');
    if (!channelIdOrHandle) {
      void this.toastNotificationService.alert({
        title: 'Missing channel',
        message: 'Enter a YouTube channel ID or handle.',
        type: 'error',
        icon: 'error',
      });
      return;
    }
    this.createPlatformAccount('youtube', () =>
      this.superSocialService.createYoutubeAccount({
        externalId: this.resolvedCreatorId(),
        businessId: this.businessId,
        channelIdOrHandle,
      }),
    );
  }

  isCreatePlatformLoading(platform: BriefTrackerSuperSocialPlatform): boolean {
    return this.createPlatformLoading()[platform] === true;
  }

  isRemoveAccountLoading(accountId: string): boolean {
    return this.removeAccountLoading()[accountId] === true;
  }

  async removeAccount(account: BriefTrackerSuperSocialAccount): Promise<void> {
    const externalId = this.resolvedCreatorId();
    if (!externalId) {
      void this.toastNotificationService.alert({
        title: 'Missing field',
        message: 'Enter creator Firebase UID above first.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: `Remove ${account.platformLabel} account?`,
      message: `This permanently deletes the ${account.platformLabel} account, snapshots, and related SuperSocial data so a new account can be connected. This cannot be undone.`,
      confirmText: 'Remove account',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });
    if (!confirmed) {
      return;
    }

    if (this.isRemoveAccountLoading(account.id)) {
      return;
    }

    const request$ = this.buildRemoveAccountRequest(account, externalId);
    if (!request$) {
      void this.toastNotificationService.alert({
        title: 'Remove not supported',
        message: `No remove endpoint for ${account.platformLabel}.`,
        type: 'warning',
        icon: 'warning',
      });
      return;
    }

    this.removeAccountLoading.update((state) => ({ ...state, [account.id]: true }));
    this.cdr.markForCheck();

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.removeAccountLoading.update((state) => {
          const next = { ...state };
          delete next[account.id];
          return next;
        });
        void this.toastNotificationService.alert({
          title: 'Account removed',
          message: `${account.platformLabel} account deleted. You can connect a new one below.`,
          type: 'success',
          icon: 'success',
        });
        this.runLookup();
      },
      error: (err: { error?: { message?: string | string[] }; message?: string }) => {
        this.removeAccountLoading.update((state) => {
          const next = { ...state };
          delete next[account.id];
          return next;
        });
        this.cdr.markForCheck();
        void this.toastNotificationService.alert({
          title: `Remove ${account.platformLabel} failed`,
          message: this.extractErrorMessage(err),
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  isSuperSocialRefreshLoading(accountId: string): boolean {
    return this.superSocialRefreshLoading()[accountId] === true;
  }

  isSuperSocialRefreshSent(accountId: string): boolean {
    return this.superSocialRefreshSent()[accountId] === true;
  }

  getHandleDraft(accountId: string): string {
    return this.handleDrafts()[accountId] ?? '';
  }

  onHandleDraftInput(accountId: string, value: string): void {
    this.handleDrafts.update((state) => ({ ...state, [accountId]: value }));
    this.cdr.markForCheck();
  }

  isHandleDraftDirty(account: BriefTrackerSuperSocialAccount): boolean {
    return isSuperSocialHandleDraftDirty(account, this.getHandleDraft(account.id));
  }

  isHandleUpdateLoading(accountId: string): boolean {
    return this.handleUpdateLoading()[accountId] === true;
  }

  updateAccountHandle(account: BriefTrackerSuperSocialAccount): void {
    const draft = normalizeSuperSocialHandleInput(this.getHandleDraft(account.id));
    if (!draft || !this.isHandleDraftDirty(account)) {
      return;
    }

    const request$ =
      account.platform === 'instagram'
        ? this.superSocialService.updateInstagramHandle(account.id, draft)
        : account.platform === 'tiktok'
          ? this.superSocialService.updateTiktokHandle(account.id, draft)
          : this.superSocialService.updateYoutubeHandle(account.id, draft);

    this.handleUpdateLoading.update((state) => ({ ...state, [account.id]: true }));
    this.cdr.markForCheck();

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.handleUpdateLoading.update((state) => ({ ...state, [account.id]: false }));
        this.applyHandleUpdateToProfile(account.id, draft);
        this.handleDrafts.update((state) => ({ ...state, [account.id]: draft }));
        this.syncShoutCreatorSocials();
        this.cdr.markForCheck();
        void this.toastNotificationService.alert({
          title: 'Handle updated',
          message: `${account.platformLabel} handle saved as @${draft}.`,
          type: 'success',
          icon: 'success',
        });
      },
      error: (err: { error?: { message?: string | string[] }; message?: string }) => {
        this.handleUpdateLoading.update((state) => ({ ...state, [account.id]: false }));
        this.cdr.markForCheck();
        void this.toastNotificationService.alert({
          title: 'Handle update failed',
          message: this.extractErrorMessage(err),
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  refreshSuperSocialAccount(account: BriefTrackerSuperSocialAccount): void {
    if (!supportsSuperSocialAccountRefresh(account.platform)) {
      return;
    }

    const request$ = this.buildSuperSocialRefreshRequest(account);
    if (!request$) {
      void this.toastNotificationService.alert({
        title: 'Refresh not supported',
        message: `No refresh orchestrator mapping for ${account.platform} source "${account.source ?? 'unknown'}".`,
        type: 'warning',
        icon: 'warning',
      });
      return;
    }

    if (this.isSuperSocialRefreshLoading(account.id)) {
      return;
    }

    this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: true }));
    this.superSocialRefreshSent.update((state) => {
      const next = { ...state };
      delete next[account.id];
      return next;
    });
    this.cdr.markForCheck();

    request$
      .pipe(
        timeout({ first: REFRESH_REQUEST_TIMEOUT_MS }),
        takeUntil(this.refreshCancelled$(account.id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: unknown) => {
          const orchestrator = result as {
            status?: string;
            refreshed_account?: boolean;
            message?: string;
          } | null;

          if (orchestrator?.status === 'deferred' || orchestrator?.refreshed_account === false) {
            this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: false }));
            this.superSocialRefreshSent.update((state) => ({ ...state, [account.id]: true }));
            this.cdr.markForCheck();
            void this.toastNotificationService.alert({
              title: 'Refresh deferred',
              message:
                orchestrator.message ??
                `${account.platformLabel} was not refreshed yet. Look up again later.`,
              type: 'warning',
              icon: 'warning',
            });
            return;
          }

          void this.toastNotificationService.alert({
            title: 'Profile refreshed',
            message: `${account.platformLabel} profile updated. Reloading…`,
            type: 'success',
            icon: 'success',
          });
          this.syncShoutCreatorSocials();
          this.reloadProfileAfterRefresh(account);
        },
        error: (err: unknown) => {
          if (!this.isSuperSocialRefreshLoading(account.id)) {
            return;
          }
          this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: false }));
          this.superSocialRefreshSent.update((state) => ({ ...state, [account.id]: true }));
          this.cdr.markForCheck();
          if (err instanceof TimeoutError) {
            void this.toastNotificationService.alert({
              title: 'Refresh timed out',
              message: `${account.platformLabel} orchestrator did not finish in time. Look up again later to check updatedDate.`,
              type: 'warning',
              icon: 'warning',
            });
            return;
          }
          void this.toastNotificationService.alert({
            title: `Error refreshing ${account.platformLabel} account`,
            message: 'Something went wrong. If the problem persists, please contact support.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private hasPlatform(platform: BriefTrackerSuperSocialPlatform): boolean {
    return this.superSocialProfile()?.accounts.some((a) => a.platform === platform) === true;
  }

  private createPlatformAccount(
    platform: BriefTrackerSuperSocialPlatform,
    requestFactory: () => Observable<unknown>,
  ): void {
    const externalId = this.resolvedCreatorId();
    if (!externalId) {
      void this.toastNotificationService.alert({
        title: 'Missing field',
        message: 'Enter creator Firebase UID above first.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.createPlatformLoading.update((state) => ({ ...state, [platform]: true }));
    this.cdr.markForCheck();

    requestFactory()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.createPlatformLoading.update((state) => ({ ...state, [platform]: false }));
          this.syncShoutCreatorSocials(externalId);
          void this.toastNotificationService.alert({
            title: `${platform} connected`,
            message: 'Account created in SuperSocial. Reloading…',
            type: 'success',
            icon: 'success',
          });
          this.runLookup();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.createPlatformLoading.update((state) => ({ ...state, [platform]: false }));
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: `Create ${platform} failed`,
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  /** Single profile GET after orchestrator confirms profile refresh — no polling. */
  private reloadProfileAfterRefresh(account: BriefTrackerSuperSocialAccount): void {
    const creatorId = this.resolvedCreatorId();
    if (!creatorId) {
      this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: false }));
      this.superSocialRefreshSent.update((state) => ({ ...state, [account.id]: true }));
      this.cdr.markForCheck();
      return;
    }

    const previousUpdatedIso = account.updatedDateIso;

    this.trackerService
      .getSuperSocialProfile(creatorId)
      .pipe(
        takeUntil(this.refreshCancelled$(account.id)),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: false }));
          this.superSocialRefreshSent.update((state) => ({ ...state, [account.id]: true }));
          this.cdr.markForCheck();
          return EMPTY;
        }),
      )
      .subscribe({
        next: (profile) => {
          this.superSocialProfile.set(profile);
          this.handleDrafts.set(buildSuperSocialHandleDrafts(profile.accounts));
          this.superSocialRefreshLoading.update((state) => ({ ...state, [account.id]: false }));

          const updated = superSocialAccountWasUpdated(profile, account.id, previousUpdatedIso);
          if (updated) {
            this.superSocialRefreshSent.update((state) => {
              const next = { ...state };
              delete next[account.id];
              return next;
            });
            this.cdr.markForCheck();
            void this.toastNotificationService.alert({
              title: 'Account refreshed',
              message: `${account.platformLabel} updatedDate is current.`,
              type: 'success',
              icon: 'success',
            });
            return;
          }

          this.superSocialRefreshSent.update((state) => ({ ...state, [account.id]: true }));
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Refresh still settling',
            message: `${account.platformLabel} orchestrator finished but updatedDate has not changed yet. Look up again in a moment.`,
            type: 'warning',
            icon: 'warning',
          });
        },
      });
  }

  private refreshCancelled$(accountId: string): Observable<unknown> {
    return this.cancelRefreshWork$.pipe(filter((id) => id === '*' || id === accountId));
  }

  private applyHandleUpdateToProfile(accountId: string, handle: string): void {
    const current = this.superSocialProfile();
    if (!current) {
      return;
    }
    const accounts = current.accounts.map((row) =>
      row.id === accountId ? applySuperSocialHandleUpdate(row, handle) : row,
    );
    this.superSocialProfile.set({ ...current, accounts });
  }

  private buildSuperSocialRefreshRequest(
    account: BriefTrackerSuperSocialAccount,
  ): Observable<unknown> | null {
    const source = (account.source ?? '').toLowerCase();

    if (account.platform === 'instagram') {
      if (source === 'meta_api') {
        return this.superSocialService.refreshInstragramAuthentic(account.id);
      }
      if (source === 'apify' || !source) {
        return this.superSocialService.refreshInstragramApify(account.id);
      }
      return null;
    }

    if (account.platform === 'tiktok') {
      if (source === 'tiktok_api') {
        return this.superSocialService.refreshTiktokTikAuthentic(account.id);
      }
      if (source === 'tik_api' || source === 'apify' || !source) {
        return this.superSocialService.refreshTiktokTikApi(account.id);
      }
      return null;
    }

    if (account.platform === 'youtube') {
      return this.superSocialService.refreshYoutubeAuthentic(account.id);
    }

    return null;
  }

  private buildRemoveAccountRequest(
    account: BriefTrackerSuperSocialAccount,
    externalId: string,
  ): Observable<unknown> | null {
    const businessId = this.businessId;

    if (account.platform === 'tiktok') {
      return this.superSocialService.removeTiktokAccount({
        externalId,
        businessId,
        tiktokAccountId: account.id,
      });
    }

    if (account.platform === 'instagram') {
      return this.superSocialService.removeInstagramAccount({
        externalId,
        businessId,
        instagramAccountId: account.id,
      });
    }

    if (account.platform === 'youtube') {
      return this.superSocialService.removeYoutubeAccount({
        externalId,
        businessId,
        youtubeAccountId: account.id,
      });
    }

    return null;
  }

  private extractErrorMessage(err: {
    error?: { message?: string | string[] };
    message?: string;
  }): string {
    const nested = err?.error?.message;
    if (Array.isArray(nested)) {
      return nested.join(', ');
    }
    if (typeof nested === 'string' && nested.length) {
      return nested;
    }
    return err?.message ?? 'Request failed';
  }

  /** Sync Shout creator row (handles + followers) from SuperSocial after refresh/connect. */
  private syncShoutCreatorSocials(creatorUid?: string): void {
    const uid = creatorUid?.trim() || this.resolvedCreatorId();
    if (!uid) {
      return;
    }
    this.superSocialService
      .syncCreatorFollowersToShout(uid)
      .pipe(
        catchError((err: unknown) => {
          console.warn('SuperSocials: syncCreatorFollowersToShout failed', err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
